import type { Entity, FieldDef, FieldValue, NodeType, StoryCtx } from '@dispositio/core';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { de } from '../../i18n/de';
import { designFoci } from '../../lib/designModel';
import { groupsOfType } from '../../lib/libraryModel';
import { useStoryEnv } from '../../state/storyEnv';
import { toast } from '../../state/toast';
import type { EntityDialog } from '../../state/workspace';
import { FieldsForm } from '../fields/FieldsForm';
import { Modal } from '../Modal';
import { FieldAdder } from './FieldAdder';
import { FieldMenu } from './FieldMenu';
import { ImageDrop } from './ImageDrop';
import { useCategoryActions } from './useCategories';

/** Mehrzahl des Bereichs einer Klasse („Personen“), für Rückfragen wie „bei allen Personen entfernen?“ */
export const pluralOf = (ctx: StoryCtx, type: NodeType): string | null => (type.category === 'foundational' ? null : (designFoci(ctx).find((f) => f.type?.id === type.id)?.label ?? type.name));

interface FormProps {
  type: NodeType;
  values: Record<string, FieldValue>;
  onChange: (key: string, value: FieldValue) => void;
  /** Eingabe des Namens (beim Anlegen sofort übernommen, beim Bearbeiten mit Autosave) */
  title: ReactNode;
  /** unter dem Namen (z. B. die Kategorie) */
  extra?: ReactNode;
  idPrefix: string;
}

/**
 * Inhalt des Fensters: Bild, Name und Kurzbeschreibung oben, darunter die weiteren Felder der Klasse und „+ Feld“. Jedes Feld hat ein
 * Menü (umbenennen, entfernen); Änderungen daran gelten für alle Einträge der Klasse.
 */
function EntityForm({ type, values, onChange, title, extra, idPrefix }: FormProps) {
  const { ctx } = useStoryEnv();
  const plural = pluralOf(ctx, type);
  const image = type.imageKey ? type.fields.find((f) => f.key === type.imageKey && f.type === 'image') : undefined;
  const hover = type.hoverKey ? type.fields.find((f) => f.key === type.hoverKey && f.key !== type.titleKey) : undefined;
  const rest = type.fields.filter((f) => f !== image && f !== hover && f.key !== type.titleKey);
  const menu = (def: FieldDef): ReactNode => <FieldMenu type={type} def={def} plural={plural} />;
  return (
    <div className="entity-form">
      <div className={`entity-head${image ? '' : ' no-image'}`}>
        {image && <ImageDrop value={typeof values[image.key] === 'string' && values[image.key] !== '' ? String(values[image.key]) : null} onChange={(id) => onChange(image.key, id)} label={image.label} />}
        <div className="entity-head-main">
          {title}
          {extra}
          {hover && <FieldsForm defs={[hover]} values={values} onChange={onChange} idPrefix={idPrefix} fieldActions={menu} />}
        </div>
      </div>
      {rest.length > 0 && <FieldsForm defs={rest} values={values} onChange={onChange} idPrefix={idPrefix} fieldActions={menu} />}
      <FieldAdder type={type} />
    </div>
  );
}

/** Kategorie eines Eintrags (nur, wenn der Bereich Kategorien hat) */
export function CategorySelect({ typeId, value, onChange, testId }: { typeId: string; value: string | null; onChange: (groupId: string | null) => void; testId: string }) {
  const { ctx } = useStoryEnv();
  const groups = groupsOfType(ctx, typeId).sort((a, b) => a.name.localeCompare(b.name, 'de'));
  if (groups.length === 0) return null;
  return (
    <label className="entity-category">
      <span className="muted small">{de.design.category}</span>
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} data-testid={testId}>
        <option value="">{de.design.noCategory}</option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function CreateEntity({ typeId, name, pos, groupId, more, onClose, onCreated }: { typeId: string; name?: string; pos?: { x: number; y: number }; groupId?: string; more: boolean; onClose: () => void; onCreated?: (entity: Entity) => void }) {
  const { ctx, actions } = useStoryEnv();
  const { moveInto } = useCategoryActions();
  const [category, setCategory] = useState<string | null>(groupId ?? null);
  const type = ctx.types.get(typeId);
  const initial = (): Record<string, FieldValue> => (type && name ? { [type.titleKey]: name } : {});
  const [values, setValues] = useState<Record<string, FieldValue>>(initial);
  // Felder mit Autosave (Kurzbeschreibung) melden ihren Text erst beim Verlassen: „Anlegen“ liest deshalb den neuesten Stand hier
  const latest = useRef(values);
  /** Durchgang: nach „Anlegen und weitere“ beginnt ein neuer mit leeren Feldern; spät eintreffende Werte des alten zählen nicht */
  const [round, setRound] = useState(0);
  const roundRef = useRef(0);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false); // Doppelklick auf „Anlegen“ legt nur einen Eintrag an
  // Klasse inzwischen gelöscht (etwa in einem anderen Fenster): das Fenster schließt sich
  useEffect(() => {
    if (!type) onClose();
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!type) return null;
  const titleKey = type.titleKey;
  const titleValue = String(values[titleKey] ?? '');

  const change = (forRound: number, key: string, value: FieldValue): void => {
    if (forRound !== roundRef.current) return;
    latest.current = { ...latest.current, [key]: value };
    setValues(latest.current);
  };

  const create = async (again: boolean): Promise<void> => {
    const v = latest.current;
    const title = String(v[titleKey] ?? '').trim();
    if (busyRef.current || title === '') return;
    busyRef.current = true;
    setBusy(true);
    try {
      const fields = Object.fromEntries(Object.entries({ ...v, [titleKey]: title }).filter(([, x]) => x !== null && x !== ''));
      const cs = await actions.createEntity({ typeId: type.id, fields, ...(pos ? { x: pos.x, y: pos.y } : {}) });
      const entity = cs?.entities[0];
      if (!entity) return;
      if (category) await moveInto(entity, category);
      onCreated?.(entity);
      if (!again) return onClose();
      toast.ok(de.design.created(title));
      roundRef.current += 1;
      latest.current = {};
      setValues({});
      setRound(roundRef.current);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const valid = titleValue.trim() !== '';
  return (
    <Modal
      title={de.design.newTitle(type.name)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {de.common.cancel}
          </button>
          <span className="grow" />
          {more && (
            <button type="button" className="btn" disabled={!valid || busy} onClick={() => void create(true)} title={de.design.createAndNextHint} data-testid="create-entity-next">
              {de.design.createAndNext}
            </button>
          )}
          <button type="button" className="btn primary" onClick={() => void create(false)} disabled={!valid || busy} data-testid="create-entity">
            {de.design.create}
          </button>
        </>
      }
    >
      <div key={round} className="col" data-testid="entity-dialog" data-mode="create">
        <EntityForm
          type={type}
          values={values}
          onChange={(k, v) => change(round, k, v)}
          idPrefix="new"
          title={
            <input
              type="text"
              className="entity-title"
              autoFocus
              value={titleValue}
              onChange={(e) => change(round, titleKey, e.target.value)}
              onKeyDown={(e) => {
                // Enter legt an (Strg+Enter: anlegen und den nächsten beginnen)
                if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
                e.preventDefault();
                void create(more && (e.ctrlKey || e.metaKey));
              }}
              placeholder={de.design.titlePlaceholder}
              aria-label={de.design.title}
              maxLength={200}
              data-testid={`new-${titleKey}`}
            />
          }
          extra={<CategorySelect typeId={type.id} value={category} onChange={setCategory} testId="new-category" />}
        />
        <div className="muted small">{de.design.createHint}</div>
      </div>
    </Modal>
  );
}

/**
 * Fenster zum Anlegen eines Eintrags (Person, Ort, Gegenstand, Organisation, Konzept …) mit dem Nötigsten (Name genügt). Bearbeitet
 * wird ein Eintrag danach in der Seitenleiste; neue Felder kommen über „+ Feld“ dazu und gelten für alle Einträge der Klasse.
 */
export function EntityModal({ dialog, onClose, onCreated, more = true }: { dialog: EntityDialog; onClose: () => void; onCreated?: (entity: Entity) => void; more?: boolean }) {
  return <CreateEntity typeId={dialog.typeId} name={dialog.name} pos={dialog.pos} groupId={dialog.groupId} more={more} onClose={onClose} onCreated={onCreated} />;
}
